import React, { useState, useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Flex,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Input,
  InputGroup,
  InputRightAddon,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import CustomAccordion from "components/Accordion/Accordion";
import { ChevronRightIcon } from "@chakra-ui/icons";
import axios from "api/axios";

const KnowledgeBase = () => {
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [articles, setArticles] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const handleGetArticles = async () => {
    try {
      const response = await axios.get("/articles/get-articles");
      if (response.data) {
        setArticles(response.data);
      }
    } catch (err) {
      console.log("Error getting articles.", err);
    }
  };

  useEffect(() => {
    handleGetArticles();
  }, []);

  const titleToTagMapping = {
    "Customer Success Manager": ["Rep", "CSM"],
    Admin: ["Admin"],
    Analytics: ["Analytics"],
    Billing: ["Billing"],
    Creative: ["Creative"],
    "CO-OP": ["CO-OP"],
    Onboarding: ["Onboarding"],
    Operations: ["Operations"],
    OTT: ["OTT"],
    Search: ["Search"],
    Social: ["Social"],
    Support: ["Support"],
    SEO: ["SEO"],
    RepMan: ["RepMan"],
  };

  const updateCounts = (items, articles) => {
    return items.map((item) => {
      const tags = titleToTagMapping[item.title] || [item.title];
      const normalizedTags = tags.map((tag) => tag.toLowerCase()); // Normalize the tags to lowercase

      const count = articles.filter((article) =>
        article.tags.some((tag) => normalizedTags.includes(tag.toLowerCase()))
      ).length;
      return { ...item, count };
    });
  };

  const items = updateCounts(
    [
      {
        title: "Customer Success Manager",
        description: "Learn how to do rep and csm stuffs.",
        count: 0,
        tag: ["CSM", "REP"],
      },
      {
        title: "Compliance",
        description: "Learn how to do Compliance stuffs.",
        count: 0,
        tag: ["Compliance", "Admin"],
      },
      {
        title: "Analytics",
        description: "Analytics Masterclass: Learn the basics of analytics.",
        count: 0,
        tag: ["Analytics"],
      },
      { title: "Billing", description: "Learn...", count: 0, tag: ["Billing"] },
      {
        title: "Creative",
        description: "Learn...",
        count: 0,
        tag: ["Creative"],
      },
      { title: "CO-OP", description: "Learn...", count: 0, tag: ["CO-OP"] },
      {
        title: "Onboarding",
        description: "Learn...",
        count: 0,
        tag: ["Onboarding"],
      },
      {
        title: "Operations",
        description: "Learn...",
        count: 0,
        tag: ["Operations"],
      },
      { title: "OTT", description: "Learn...", count: 0, tag: ["OTT"] },
      { title: "Search", description: "Learn...", count: 0, tag: ["Search"] },
      { title: "Social", description: "Learn...", count: 0, tag: ["Social"] },
      { title: "Support", description: "Learn...", count: 0, tag: ["Support"] },
      { title: "SEO", description: "Learn...", count: 0, tag: ["SEO"] },
      { title: "RepMan", description: "Learn...", count: 0, tag: ["RepMan"] },
    ],
    articles
  );

  const handleCardClick = (title) => {
    const tags = titleToTagMapping[title] || [title]; // Ensure tags are always an array
    const filteredItems = articles.filter((article) =>
      article.tags.some((tag) =>
        tags.map((t) => t.toLowerCase()).includes(tag.toLowerCase())
      )
    );
    const articleIds = filteredItems.map((article) => article._id);
    navigate("articles", {
      state: { articleIds }, // Passing article IDs to Articles.js
    });
  };

  const handleSearch = () => {
    const filteredItems = articles.filter(
      (item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.tags.some((tag) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        )
    );
    const articleIds = filteredItems.map((article) => article._id);
    console.log("Searching articles with IDs:", articleIds);
    navigate("articles", {
      state: { articleIds }, // Passing article IDs to Articles.js
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const renderBreadcrumb = () => {
    const pathnames = location.pathname
      .split("/")
      .filter((x) => x && x !== "landing" && x !== "knowledgebase");
    return (
      <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="white" />}>
        <BreadcrumbItem color={"white"}>
          <BreadcrumbLink color={"white"} as={Link} to="/admin/dashboard">
            Dashboard
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink color={"white"} as={Link} to="/landing/knowledgebase">
            Knowledge Base
          </BreadcrumbLink>
        </BreadcrumbItem>
        {pathnames.map((value, index) => {
          const to = `/landing/knowledgebase/${pathnames
            .slice(0, index + 1)
            .join("/")}`;
          return (
            <BreadcrumbItem key={to}>
              <BreadcrumbLink color={"white"} as={Link} to={to}>
                {value.charAt(0).toUpperCase() + value.slice(1)}
              </BreadcrumbLink>
            </BreadcrumbItem>
          );
        })}
      </Breadcrumb>
    );
  };

  const searchBgColor = useColorModeValue("white", "#2D3748");
  const searchTextColor = useColorModeValue("black", "white");
  const searchBorderColor = useColorModeValue("blue.500", "blue.300");

  return (
    <>
      <Box
        py={5}
        px={{ base: "20px", md: "50px", lg: "200px" }}
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        {renderBreadcrumb()}
        <Button size="sm" onClick={() => navigate("new-post")}>
          create
        </Button>
      </Box>
      <Box w={"100%"} px={{ base: "20px", md: "50px", lg: "200px" }}>
        <Flex direction={{ base: "column", lg: "row" }}>
          <CustomAccordion items={items} handleCardClick={handleCardClick} />
          <Box flex="1" mx={"20px"}>
            <Box pb={"20px"}>
              <InputGroup size={"lg"}>
                <Input
                  bg={searchBgColor}
                  color={searchTextColor}
                  placeholder="Enter a question here to search our knowledge base for answers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  borderColor={searchBorderColor}
                />
                <InputRightAddon
                  bg={"blue.400"}
                  borderColor={searchBorderColor}
                  color={"white"}
                  _hover={{ cursor: "pointer" }}
                  onClick={handleSearch}
                >
                  Search
                </InputRightAddon>
              </InputGroup>
            </Box>
            <Box pb={10}>
              <Outlet
                context={{
                  items,
                  articles,
                  handleCardClick,
                  handleGetArticles,
                }}
              />
            </Box>
          </Box>
        </Flex>
      </Box>
    </>
  );
};

export default KnowledgeBase;
